import React from 'react'
import PropTypes from 'prop-types'
import styled, { keyframes } from 'styled-components'
import device from '../lib/mediaQueries'

const move = keyframes`
    to {
      transform: translate3d(15vw, 12vh, 0) rotate(250deg);
    }
`

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`

const Difference = styled.div`
  top: ${(props) => props.top};
  left: ${(props) => props.left};
  position: absolute;
  width: 250px;
  height: 250px;
  background-color: ${(props) => props.theme.inverted};
  border-radius: 50%;
  mix-blend-mode: difference;
  pointer-events: none;
  animation: ${fadeIn} 0.15s ease-out, ${move} 120s linear alternate infinite;
  z-index: 10;

  ${device.laptop} {
    width: 350px;
    height: 350px;
  }
`

// Rendered only on the client, after mount. It never exists in the server HTML,
// so it can't participate in the first-load layout/hydration and shift anything.
// It stays `position: absolute` (not fixed) so `mix-blend-mode: difference`
// still inverts the page content behind it, and because it's out of flow,
// mounting it after hydration costs nothing in layout.
const DifferenceObject = ({ className, avoidRef }) => {
  const [position, setPosition] = React.useState(null)

  React.useEffect(() => {
    // Randomize the position on each mount — Gatsby remounts page components on
    // navigation, so the circle lands somewhere new every time you land on a
    // page. This runs client-only (the element never exists in the server HTML),
    // so `Math.random` can't cause an SSR hydration mismatch.
    const rand = (min, max) => min + Math.random() * (max - min)

    // When `avoidRef` is given (case study pages), keep the circle inside the
    // hero region but NOT overlapping the hero image. We measure the hero and
    // the image relative to the positioned ancestor (LayoutContainer) the
    // absolute circle resolves against, then rejection-sample a spot within the
    // hero that clears the image's box. gatsby-image reserves the image's size
    // up front, so this measures correctly before the image finishes loading.
    const hero = avoidRef && avoidRef.current
    const parent = hero && hero.offsetParent
    const imageEl =
      hero &&
      (hero.querySelector('.gatsby-image-wrapper') || hero.querySelector('img'))

    if (hero && parent && imageEl) {
      const size = window.innerWidth >= 900 ? 350 : 250
      const pad = 12
      const parentRect = parent.getBoundingClientRect()
      const heroRect = hero.getBoundingClientRect()
      const imgRect = imageEl.getBoundingClientRect()

      // Positions relative to the offset parent's top-left.
      const heroTop = heroRect.top - parentRect.top
      const heroBottom = heroRect.bottom - parentRect.top
      const img = {
        left: imgRect.left - parentRect.left - pad,
        right: imgRect.right - parentRect.left + pad,
        top: imgRect.top - parentRect.top - pad,
        bottom: imgRect.bottom - parentRect.top + pad,
      }

      // Expand the sampling area beyond the strict hero box so placements don't
      // feel constrained: let it drift down past the hero, and bleed slightly
      // past the left/right edges (LayoutContainer clips overflow, so partial
      // off-screen placements just read as the circle peeking in from an edge).
      const expandDown = Math.round(window.innerHeight * 0.3)
      const edgeBleed = Math.round(size * 0.25)
      const minLeft = -edgeBleed
      const maxLeft = parentRect.width - size + edgeBleed
      const minTop = Math.max(0, heroTop)
      const maxTop = Math.max(minTop, heroBottom - size + expandDown)

      let placed = null
      for (let i = 0; i < 40; i += 1) {
        const leftPx = rand(minLeft, maxLeft)
        const topPx = rand(minTop, maxTop)
        const clears =
          leftPx + size < img.left ||
          leftPx > img.right ||
          topPx + size < img.top ||
          topPx > img.bottom
        if (clears) {
          placed = { topPx, leftPx }
          break
        }
      }
      // Fallback: hug the hero's top-left corner, which is always clear of the
      // image (image sits right on desktop, below on mobile).
      setPosition(placed || { topPx: heroTop, leftPx: 0 })
      return
    }

    // Otherwise (landing, work, 404) pick uniformly across a wide range so the
    // spread is large: `top` stays within the first viewport (above the fold)
    // and `left` stays on-screen.
    setPosition({
      top: rand(4, 45),
      left: rand(5, 85),
    })
  }, [avoidRef])

  if (!position) return null

  // Measured placements are in px; the default random placement uses `vh`/`%`
  // (`vh` keeps it viewport-anchored so it doesn't drift as images load).
  return (
    <Difference
      className={className}
      top={position.topPx != null ? `${position.topPx}px` : `${position.top}vh`}
      left={
        position.leftPx != null ? `${position.leftPx}px` : `${position.left}%`
      }
    />
  )
}

DifferenceObject.propTypes = {
  className: PropTypes.string,
  avoidRef: PropTypes.shape({ current: PropTypes.any }),
}

DifferenceObject.defaultProps = {
  className: undefined,
  avoidRef: undefined,
}

export default DifferenceObject
